import { SUP_VALIDACAO } from "@caint/data";
import type { NivelMaturidade } from "@caint/engine";
import type {
  EstratoAmostra,
  ItemCatalogo,
  LikertPonto,
  ParecerConfiabilidade,
  ResultadoSupervisao,
  SupAmostraItem,
  SupCriterioResp,
  SupervisaoData,
} from "@caint/shared";

/**
 * Indicadores PUROS da Supervisão AUDIN (doc 09), Camada 3: diagnóstico de
 * amostragem, conformidade agregada de uma fase de processo e os indicadores do
 * parecer de confiabilidade. Promovidos do frontend para uso compartilhado
 * (frontend + backend). Sem IO — mesma entrada, mesma saída.
 */

/**
 * Limite (default) da amostra de re-performance cega por ciclo. Aqui é o
 * tamanho-alvo (plano) usado pelo diagnóstico de amostragem; a geração da
 * amostra vive no chamador (frontend). Config nomeada (doc 13 §13.8).
 */
export const TAMANHO_AMOSTRA = 28;

/**
 * Converte `mat` (escala 0-4/NA) em C/NC/NA para compatibilidade com a lógica
 * de conformidade legacy. Corte: mat ≥ 3 → C; mat ≤ 2 → NC; na → NA.
 */
export function matParaResultado(mat: NivelMaturidade | null | undefined): ResultadoSupervisao | null {
  if (!mat) return null;
  if (mat === "na") return "NA";
  return Number(mat) >= 3 ? "C" : "NC";
}

/** Score ONA do critério (0–1) a partir da escala 0-4/NA unificada. */
const ONA_SCORE: Readonly<Record<string, number>> = {
  "0": 0.0,
  "1": 0.2,
  "2": 0.5,
  "3": 0.8,
  "4": 1.0,
};

/** Corroboração entre a maturidade do supervisor (cega) e a nota do avaliador. */
export function corroboracao(
  matSup: string | null | undefined,
  notaAval: string | null | undefined,
): "Corrobora" | "Diverge" | null {
  if (!matSup || !notaAval) {
    return null;
  }
  if (matSup === "na" || notaAval === "na") {
    return matSup === notaAval ? "Corrobora" : "Diverge";
  }
  return Math.abs(Number(matSup) - Number(notaAval)) <= 1 ? "Corrobora" : "Diverge";
}

/** Regra de decisão sugerida do parecer (doc §9.5). */
export function sugerirParecer(metaNcAlta: number, taxa: number | null): ParecerConfiabilidade | null {
  if (taxa == null) {
    return null; // sem amostra mesclada ainda
  }
  if (metaNcAlta === 0 && taxa >= 0.9) {
    return "Confiavel";
  }
  if (metaNcAlta <= 1 && taxa >= 0.75) {
    return "Ressalvas";
  }
  return "NaoConfiavel";
}

/** Resposta corrente de um critério (ou vazia). */
export function criterioOu(dados: SupervisaoData, num: string): SupCriterioResp {
  return dados.criterios[num] ?? { resultado: null };
}

/* ------------------------- CONFORMIDADE DE FASE ------------------------- */

/** Escala Likert de aderência (aba Campo) + corte para conformidade. */
export interface EscalaLikert {
  /** Pontos da escala (1 → 5), com rótulo legível ao supervisor. */
  readonly pontos: readonly { readonly valor: LikertPonto; readonly rotulo: string }[];
  /** Corte: Likert ≥ `corteConforme` → C; abaixo → NC. NA fica fora da escala. */
  readonly corteConforme: LikertPonto;
}

export interface PesosConformidade {
  /**
   * Score de cada resultado no índice (0–1). `null` = resultado fora do
   * numerador E do denominador: é o caso do NA, que não penaliza nem infla
   * a conformidade (coerência com doc 11 §3.3 e o motor — NA é excluído).
   */
  readonly resultado: Readonly<Record<ResultadoSupervisao, number | null>>;
  /** Peso por criticidade — espelha a escala de criticidade do domínio. */
  readonly criticidade: Readonly<Record<string, number>>;
  /** Escala Likert da aba Campo e seu mapa para C/NC (mesma fonte de config). */
  readonly likert: EscalaLikert;
}

/**
 * Fonte ÚNICA de parâmetros de conformidade da fase CAMPO (doc 13 §13.8): pesos
 * de C/NC/NA, pesos por criticidade E a escala Likert + corte Likert→C/NC. C
 * conforme, NC não-conforme, NA neutro; criticidade Alta=3/Média=2/Baixa=1;
 * Likert de aderência ≥ 4 → C, ≤ 3 → NC. Ver docs/contexto/16-*.md.
 */
export const PESOS_CONFORMIDADE_CAMPO: PesosConformidade = Object.freeze({
  resultado: Object.freeze({ C: 1, NC: 0, NA: null }),
  criticidade: Object.freeze({ Alta: 3, Média: 2, Baixa: 1 }),
  likert: Object.freeze({
    pontos: Object.freeze([
      { valor: 1, rotulo: "Não atende" },
      { valor: 2, rotulo: "Atende pouco" },
      { valor: 3, rotulo: "Atende parcialmente" },
      { valor: 4, rotulo: "Atende em grande parte" },
      { valor: 5, rotulo: "Atende plenamente" },
    ] as const),
    corteConforme: 4,
  }),
});

/** Peso de criticidade de um critério (fallback 1 p/ criticidade desconhecida). */
export function pesoCriticidade(crit: string, pesos: PesosConformidade = PESOS_CONFORMIDADE_CAMPO): number {
  return pesos.criticidade[crit] ?? 1;
}

/** Subtotal de um resultado: nº de critérios e soma dos pesos de criticidade. */
export interface SubtotalResultado {
  readonly itens: number;
  readonly peso: number;
}

/** Conformidade agregada de uma fase de critérios de processo (PRÉ-VISITA / CAMPO). */
export interface ConformidadeFase {
  readonly total: number; // critérios vigentes na fase
  readonly respondidos: number; // quantos têm resultado C/NC/NA
  readonly porResultado: Readonly<Record<ResultadoSupervisao, SubtotalResultado>>;
  readonly indice: number | null; // 0–1 ponderado por criticidade; NA fora. null se nada pondera
  readonly indiceSimples: number | null; // 0–1 sem ponderação: C / (C+NC). null se nada respondido
}

/**
 * Agrega C/NC/NA de uma fase iterando SEMPRE a lista estática vigente
 * ({@link import("@caint/data").SUP_CAMPO} et al.) — respostas órfãs no blob
 * (ex.: critérios removidos do catálogo) são ignoradas por construção, então
 * supervisões antigas carregam sem quebrar e não inflam a contagem.
 *
 * Índice consolidado = Σ(score(resultado)·peso(criticidade)) / Σ(peso(criticidade)),
 * sobre os critérios cujo resultado pondera (C/NC). NA fica fora do numerador e
 * do denominador. Função pura (doc 13 §13.1); pesos parametrizáveis.
 */
export function conformidadeFase(
  lista: readonly { readonly id: string; readonly crit: string }[],
  dados: SupervisaoData,
  pesos: PesosConformidade = PESOS_CONFORMIDADE_CAMPO,
): ConformidadeFase {
  const porResultado: Record<ResultadoSupervisao, { itens: number; peso: number }> = {
    C: { itens: 0, peso: 0 },
    NC: { itens: 0, peso: 0 },
    NA: { itens: 0, peso: 0 },
  };
  let num = 0; // Σ score·peso (ponderado)
  let den = 0; // Σ peso dos que ponderam
  let cSimples = 0;
  let ncSimples = 0;
  for (const item of lista) {
    const crit = criterioOu(dados, item.id);
    const peso = pesoCriticidade(item.crit, pesos);

    if (crit.mat != null) {
      // Nova escala unificada (0-4/NA): usa score ONA granular.
      if (crit.mat === "na") {
        porResultado.NA.itens++;
        porResultado.NA.peso += peso;
        continue;
      }
      const score = ONA_SCORE[crit.mat] ?? 0;
      const res = matParaResultado(crit.mat) ?? "NC";
      porResultado[res].itens++;
      porResultado[res].peso += peso;
      num += score * peso;
      den += peso;
      if (res === "C") cSimples++;
      else ncSimples++;
    } else {
      // Legado C/NC/NA.
      const res = crit.resultado;
      if (!res) continue;
      porResultado[res].itens++;
      porResultado[res].peso += peso;
      const score = pesos.resultado[res];
      if (score === null) continue; // NA: fora do numerador e do denominador
      num += score * peso;
      den += peso;
      if (res === "C") cSimples++;
      else if (res === "NC") ncSimples++;
    }
  }
  const respondidos = porResultado.C.itens + porResultado.NC.itens + porResultado.NA.itens;
  const denomSimples = cSimples + ncSimples;
  return {
    total: lista.length,
    respondidos,
    porResultado,
    indice: den > 0 ? num / den : null,
    indiceSimples: denomSimples > 0 ? cSimples / denomSimples : null,
  };
}

/* -------------------------- DIAGNÓSTICO DE AMOSTRAGEM -------------------------- */

/**
 * Diagnóstico do CÁLCULO de amostragem (apoio ao VT.P06): compara a amostra real
 * com o plano (tamanho-alvo) e mede a cobertura dos estratos. Orientativo — NÃO
 * decide o C/NC/NA. Função pura (doc 13 §13.1).
 */
export interface DiagnosticoAmostragem {
  readonly real: number; // itens na amostra
  readonly plano: number; // tamanho-alvo (TAMANHO_AMOSTRA)
  readonly atingiuPlano: boolean; // real >= plano
  readonly porEstrato: Readonly<Record<EstratoAmostra, number>>;
  readonly estratosCobertos: number; // estratos de campo (CORE/Risco/Aleatorio) com ≥1 item
  readonly temDados: boolean; // amostra já gerada?
}

/** Estratos esperados na amostra cega de campo (Divergencia é Estrato B, à parte). */
const ESTRATOS_CAMPO: readonly EstratoAmostra[] = ["CORE", "RiscoInerente", "Aleatorio"];

export function diagnosticoAmostragem(
  amostra: readonly SupAmostraItem[],
  plano: number = TAMANHO_AMOSTRA,
): DiagnosticoAmostragem {
  const porEstrato: Record<EstratoAmostra, number> = {
    CORE: 0,
    RiscoInerente: 0,
    Aleatorio: 0,
    Divergencia: 0,
  };
  for (const a of amostra) {
    porEstrato[a.estrato]++;
  }
  return {
    real: amostra.length,
    plano,
    atingiuPlano: amostra.length >= plano,
    porEstrato,
    estratosCobertos: ESTRATOS_CAMPO.filter((e) => porEstrato[e] > 0).length,
    temDados: amostra.length > 0,
  };
}

/* --------------------------- INDICADORES DO PARECER --------------------------- */

/** Critérios de Validação de ALTO risco (crit "Alta"), derivados do seed
 * `SUP_VALIDACAO` — um NC aqui é meta-crítico para o parecer (1 → Ressalvas;
 * ≥2 → Não Confiável, ver `sugerirParecer`). Generaliza o antigo conjunto fixo
 * VT.P*, acompanhando o checklist de validação vigente (3 etapas da AUDIN). */
const VALIDACAO_ALTA: ReadonlySet<string> = new Set(
  SUP_VALIDACAO.filter((i) => i.crit === "Alta").map((i) => i.id),
);

export interface IndicadoresParecer {
  metaNcAlta: number; // meta-critérios VT.P de alta criticidade em NC
  taxaCorroboracao: number | null; // % de itens da amostra que corroboram (0–1)
  itensRevisar: number; // itens da amostra encaminhados a "Revisar"
  amostraMerged: number; // quantos itens já têm nota do avaliador (merge feito)
  pareceSugerido: ParecerConfiabilidade | null;
}

/** Indicadores da Camada 3 a partir do estado da supervisão + notas do avaliador. */
export function indicadoresParecer(
  dados: SupervisaoData,
  itensAvaliacao: readonly ItemCatalogo[],
): IndicadoresParecer {
  let metaNcAlta = 0;
  for (const [num, r] of Object.entries(dados.criterios)) {
    const resultado = r.mat != null ? matParaResultado(r.mat) : r.resultado;
    if (resultado === "NC" && VALIDACAO_ALTA.has(num)) {
      metaNcAlta++;
    }
  }
  const notaPorItem = new Map(itensAvaliacao.map((it) => [it.id, it.mi ?? null]));
  let corrobora = 0;
  let merged = 0;
  let itensRevisar = 0;
  for (const a of dados.amostra) {
    if (a.status === "Revisar") {
      itensRevisar++;
    }
    const c = corroboracao(a.matSup, notaPorItem.get(a.itemId) ?? null);
    if (c) {
      merged++;
      if (c === "Corrobora") {
        corrobora++;
      }
    }
  }
  const taxa = merged > 0 ? corrobora / merged : null;
  return {
    metaNcAlta,
    taxaCorroboracao: taxa,
    itensRevisar,
    amostraMerged: merged,
    pareceSugerido: sugerirParecer(metaNcAlta, taxa),
  };
}
